import { useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { breadcrumbConfig } from '../config/breadcrumbConfig';
import { Crumb } from '../components/ui/AfaBreadcrumbs/AfaBreadcrumbs';

/**
 * Hook para generar breadcrumbs automáticamente basado en la ruta actual
 * Maneja rutas con parámetros dinámicos y construye la jerarquía completa
 * 
 * @returns Array de Crumb items para AfaBreadcrumbs
 */
export function useBreadcrumbs(): Crumb[] {
  const location = useLocation();
  const params = useParams();

  return useMemo(() => {
    const breadcrumbs: Crumb[] = [];
    let currentPath = location.pathname;

    // Función para encontrar la configuración de una ruta que puede tener parámetros
    const findRouteConfig = (path: string) => {
      // Primero buscar coincidencia exacta
      if (breadcrumbConfig[path]) {
        return { config: breadcrumbConfig[path], exactPath: path };
      }

      // Buscar rutas con parámetros dinámicos
      for (const [configPath, config] of Object.entries(breadcrumbConfig)) {
        if (configPath.includes(':')) {
          // Convertir /tramits/detall/:id en regex /tramits/detall/[^/]+
          const pattern = configPath.replace(/:[^/]+/g, '[^/]+');
          const regex = new RegExp(`^${pattern}$`);
          
          if (regex.test(path)) {
            return { config, exactPath: configPath };
          }
        }
      }

      return null;
    };

    // Construir breadcrumbs recursivamente
    const buildBreadcrumbs = (path: string): void => {
      const result = findRouteConfig(path);
      
      if (!result) return;

      const { config } = result;

      // Si tiene padre, construir el padre primero (recursión)
      if (config.parent) {
        buildBreadcrumbs(config.parent);
      }

      // Agregar el breadcrumb actual
      let label = config.title;

      // Si debe mostrar parámetro y tiene :id en la ruta
      if (config.showParam && params.id) {
        label = `${label} (${params.id})`;
      }

      const isCurrentPage = path === currentPath;

      if (isCurrentPage) {
        breadcrumbs.push({ label });
      } else {
        breadcrumbs.push({ label, to: path });
      }
    };

    buildBreadcrumbs(currentPath);

    return breadcrumbs;
  }, [location.pathname, params]);
}